import { xliff12ToJs, XliffPlaceholder, XliffTarget } from './xliff';

/** The translation map shape `loadTranslations()` from @angular/localize expects. */
export type Translations = Record<string, string>;

const NG_RESOURCE = 'ng2.template';

function placeholderToToken(placeholder: XliffPlaceholder): string {
    const [first] = Object.keys(placeholder);
    const id = first ? placeholder[first]?.id : undefined;

    // $localize expects `{$PLACEHOLDER}` where the XLIFF carries `<x id="PLACEHOLDER"/>`.
    return id ? `{$${id}}` : '';
}

function targetToString(target: XliffTarget): string {
    if (typeof target === 'string') return target;

    return target.map(part => (typeof part === 'string' ? part : placeholderToToken(part))).join('');
}

/**
 * Converts an XLIFF 1.2 document into the JSON map `loadTranslations()` consumes.
 *
 * Units without a `<target>` are skipped, which leaves the source text (German) in place —
 * the correct fallback for a not-yet-translated unit.
 */
export async function xliffToJson(xliff: string): Promise<Translations> {
    const parsed = await xliff12ToJs(xliff, { captureSpacesBetweenElements: true });
    const units = parsed.resources[NG_RESOURCE];

    if (!units) {
        throw new Error(`Could not parse XLIFF: no "${NG_RESOURCE}" resource found`);
    }

    const translations: Translations = {};

    for (const [id, unit] of Object.entries(units)) {
        const { target } = unit;

        if (target === undefined) continue;

        if (typeof target !== 'string' && !Array.isArray(target)) {
            throw new Error(`Could not parse XLIFF unit "${id}": ${JSON.stringify(target)}`);
        }

        translations[id] = targetToString(target);
    }

    return translations;
}
